"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Duration = void 0;
const PublicConfig_1 = require("../Config/PublicConfig");
const String_1 = require("./String");
const Utils_1 = require("./Utils");
const ZonedDateTime_1 = require("./ZonedDateTime");
class Duration {
    constructor(ms) {
        this._ms = Math.round(ms);
    }
    /**
     * Constructors
     */
    static fromMilliseconds(ms) {
        return new Duration(ms);
    }
    static fromSeconds(s) {
        return new Duration(s * 1000);
    }
    static fromMinutes(m) {
        return new Duration(m * 60 * 1000);
    }
    static fromHours(h) {
        return new Duration(h * 60 * 60 * 1000);
    }
    static fromDays(d) {
        return new Duration(d * 24 * 60 * 60 * 1000);
    }
    static since(date) {
        let epochMs;
        if (date instanceof Date) {
            epochMs = date.getTime();
        }
        else if (date instanceof ZonedDateTime_1.ZonedDateTime) {
            epochMs = date.epochMs();
        }
        else {
            epochMs = date;
        }
        return new Duration(Date.now() - epochMs);
    }
    static between(start, end) {
        let startEpochMs;
        if (start instanceof Date) {
            startEpochMs = start.getTime();
        }
        else if (start instanceof ZonedDateTime_1.ZonedDateTime) {
            startEpochMs = start.epochMs();
        }
        else {
            startEpochMs = start;
        }
        let endEpochMs;
        if (end instanceof Date) {
            endEpochMs = end.getTime();
        }
        else if (end instanceof ZonedDateTime_1.ZonedDateTime) {
            endEpochMs = end.epochMs();
        }
        else {
            endEpochMs = end;
        }
        return new Duration(endEpochMs - startEpochMs);
    }
    /**
     * Conversion
     */
    asMilliseconds() {
        return this._ms;
    }
    asSeconds() {
        return this._ms / 1000.0;
    }
    asMinutes() {
        return this.asSeconds() / 60.0;
    }
    asHours() {
        return this.asMinutes() / 60.0;
    }
    asDays() {
        return this.asHours() / 24.0;
    }
    /**
     * Arithmetic
     */
    plus(d) {
        return new Duration(this._ms + d._ms);
    }
    minus(d) {
        return new Duration(this._ms - d._ms);
    }
    times(factor) {
        return new Duration(Math.round(this._ms * factor));
    }
    equals(other) {
        return this._ms === other._ms;
    }
    isGreaterThan(other, strictly = true) {
        return strictly ? this._ms > other._ms : this._ms >= other._ms;
    }
    isLessThan(other, strictly = true) {
        return strictly ? this._ms < other._ms : this._ms <= other._ms;
    }
    toJSON() {
        if (this._ms % 3600000 === 0) {
            return `${this._ms / 3600000} hrs`;
        }
        else if (this._ms % 60000 === 0) {
            return `${this._ms / 60000} min`;
        }
        else if (this._ms % 1000 === 0) {
            return `${this._ms / 1000} sec`;
        }
        else {
            return `${this._ms} ms`;
        }
    }
    /**
     * Formats a duration.
     * Examples with separatedDays disabled: "09:12:59", "128:12:59.345"
     * Examples with separatedDays="d": "5d08:12:59.34", "5d08:12:59"
     */
    toString(fractionalSecondDigits = 3, separatedDays) {
        let ms = this._ms > 0 ? this._ms : -this._ms;
        let ret = "";
        if (separatedDays) {
            const days = Math.floor(ms / (24 * 3600 * 1000));
            ms %= 24 * 3600 * 1000;
            ret = `${days}${separatedDays}`;
        }
        const h = Math.floor(ms / (3600 * 1000));
        ms %= 3600 * 1000;
        const m = Math.floor(ms / (60 * 1000));
        ms %= 60 * 1000;
        const s = Math.floor(ms / 1000);
        ms %= 1000;
        ret += `${(0, String_1.padZeroes)(h, 2)}:${(0, String_1.padZeroes)(m, 2)}:${(0, String_1.padZeroes)(s, 2)}`;
        if (fractionalSecondDigits > 0) {
            ret += `.${(0, String_1.padZeroes)(ms, 3).slice(0, fractionalSecondDigits)}`;
        }
        return this._ms < 0 ? `-${ret}` : ret;
    }
    /**
     * Loosely formats a duration in readable human form.
     *
     * Option 'zeroToleranceSec' defines a threshold below which the duraiotn is considered zero.
     * Option 'numeric' controls if output should be "1 day ago" VS "yesterday"
     *
     * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#parameters
     * for more details about options.
     *
     * Absolute examples: "3 weeks", "une semaine", "11 days", "0 seconds", "one second"
     * Relative examples: "5 days ago", "7 hours ago", "now", "in 23 minutes", "dans 11 jours"
     */
    toRoundedLocaleString(options) {
        var _a, _b, _c, _d, _e, _f;
        const tokens = this.toRoundedStringHelper(options);
        try {
            if (tokens.unit === "zero") {
                if (options === null || options === void 0 ? void 0 : options.relative) {
                    const fmt = new Intl.RelativeTimeFormat((_a = options === null || options === void 0 ? void 0 : options.locale) !== null && _a !== void 0 ? _a : (0, PublicConfig_1.publicConfig)().i18n.fallbackLng, {
                        numeric: "auto",
                    });
                    return fmt.format(0, "second");
                }
                tokens.unit = "second";
            }
            const rtfOpts = {
                numeric: (_b = options === null || options === void 0 ? void 0 : options.numeric) !== null && _b !== void 0 ? _b : "always",
                style: (_c = options === null || options === void 0 ? void 0 : options.style) !== null && _c !== void 0 ? _c : "long",
            };
            const rtf = new Intl.RelativeTimeFormat((_e = (_d = options === null || options === void 0 ? void 0 : options.locale) === null || _d === void 0 ? void 0 : _d.toLowerCase()) !== null && _e !== void 0 ? _e : (0, PublicConfig_1.publicConfig)().i18n.fallbackLng, rtfOpts);
            let ret = rtf.format((tokens.sign === "future" ? 1 : -1) * tokens.absoluteVal, tokens.unit);
            if (options === null || options === void 0 ? void 0 : options.relative) {
                return ret;
            }
            // TODO: replace the following hack when Temporal.Duration is available in javascript
            ret = ret.replace(/in | ago|il y a |dans |vor |dentro de |hace |tra | fa/i, "");
            if (tokens.sign === "future") {
                return ret;
            }
            const pos = ret.search(/[0-9]/);
            return ret.slice(0, pos) + "-" + ret.slice(pos);
        }
        catch (err) {
            // assume this error is due to an unsupported/malformed locale so retry once with English
            if (((_f = options === null || options === void 0 ? void 0 : options.locale) === null || _f === void 0 ? void 0 : _f.toLowerCase()) !== "en") {
                (0, Utils_1.logNodeOnly)(`Duration.toRoundedStringHelper() failed with locale ${JSON.stringify(options === null || options === void 0 ? void 0 : options.locale)} - retrying with EN`, { level: "warn" });
                return this.toRoundedLocaleString(Object.assign(Object.assign({}, options), { locale: "en" }));
            }
            else {
                throw err;
            }
        }
    }
    /**
     * Unit follows https://tc39.es/ecma402/#sec-singularrelativetimeunit
     */
    toRoundedStringHelper(options) {
        var _a;
        let sec = this.asSeconds();
        const ret = {
            absoluteVal: 0,
            sign: sec >= 0 ? "future" : "past",
            unit: "zero",
        };
        sec = Math.abs(sec);
        if (sec <= ((_a = options === null || options === void 0 ? void 0 : options.zeroToleranceSec) !== null && _a !== void 0 ? _a : -1)) {
            return ret;
        }
        const years = sec / (365.25 * 24 * 60 * 60);
        const months = sec / (30.5 * 24 * 60 * 60);
        const weeks = sec / (7 * 24 * 60 * 60);
        const days = sec / (24 * 60 * 60);
        const hours = sec / (60 * 60);
        const minutes = sec / 60;
        if (years >= 1.7) {
            ret.absoluteVal = Math.round(years);
            ret.unit = "year";
        }
        else if (months >= 1.8) {
            ret.absoluteVal = Math.round(months);
            ret.unit = "month";
        }
        else if (weeks >= 1.8) {
            ret.absoluteVal = Math.round(weeks);
            ret.unit = "week";
        }
        else if (days >= 1.5) {
            ret.absoluteVal = Math.round(days);
            ret.unit = "day";
        }
        else if (hours >= 1.7) {
            ret.absoluteVal = Math.round(hours);
            ret.unit = "hour";
        }
        else if (minutes >= 1.5) {
            ret.absoluteVal = Math.round(minutes);
            ret.unit = "minute";
        }
        else {
            ret.absoluteVal = Math.round(sec);
            ret.unit = "second";
        }
        return ret;
    }
}
exports.Duration = Duration;
Duration.Zero = Duration.fromMilliseconds(0);
//# sourceMappingURL=Duration.js.map